import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "image_url": "https://example.com/person.jpg",
 *     "template_id": "am-20240901xxxxxx"
 *   },
 *   "parameters": {
 *     "use_ref_img_bg": false,
 *     "video_ratio": "9:16"
 *   }
 * }
 * ```
 */
export interface AlibabaAnimateAnyoneVideoParams {
  /**
   * 输入参数，包含人物图片URL和动作模板ID
   */
  input: {
    /**
     * 人物图片URL，必须是有效的HTTP或HTTPS URL，系统会自动转换为Base64格式。要求：文件大小<5MB，分辨率最大长度≤4096px，最小长度≥224px，宽高比0.5-2.0之间
     */
    image_url: string;
    /**
     * 动作模板ID。⚠️ 必须是真实有效的模板ID，示例值（如 am-20240901xxxxxx）无效。获取方式：(1) 通过 alibaba_animate_anyone_template 接口生成，或 (2) 使用阿里云预定义的模板ID
     */
    template_id: string;
  };
  /**
   * 可选参数，配置视频生成选项
   */
  parameters?: {
    /**
     * 是否使用参考图片的背景，默认false
     * @default false
     */
    use_ref_img_bg?: boolean;
    /**
     * 视频宽高比，可选值：9:16 或 3:4，默认9:16
     * @default "9:16"
     * @values 9:16 | 3:4
     */
    video_ratio?: '9:16' | '3:4';
  };
}

export interface AlibabaAnimateAnyoneVideoResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_animate_anyone_video
 *
 * Alibaba AnimateAnyone 视频生成接口，基于人物图片和动作模板生成人物动作视频。
 * 
 * Model: animate-anyone-gen2
 * Features:
 * - 将静态人物图片转换为动态视频
 * - 支持使用预定义或自定义动作模板
 * - 可选择保留原图背景
 * - 支持多种视频宽高比
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/animateanyone-video-generation-api
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaAnimateAnyoneVideo } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaAnimateAnyoneVideo(
 *   {
 *     "input": {
 *       "image_url": "https://example.com/person.jpg",
 *       "template_id": "am-20240901xxxxxx"
 *     },
 *     "parameters": {
 *       "use_ref_img_bg": false,
 *       "video_ratio": "9:16"
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaAnimateAnyoneVideoParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaAnimateAnyoneVideo(
  params: AlibabaAnimateAnyoneVideoParams,
): Promise<AlibabaAnimateAnyoneVideoResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_animate_anyone_video',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaAnimateAnyoneVideoResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
